//* Import libraries
import { useState, useCallback } from "react";

//* Import react-query hooks
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//* Import API
import { collaboratorAPI } from "./collaboratorAPI";

//* Import Pagination type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
  type CollaboratorsDetails,
  type CreateCollaboratorInput,
  type UpdateCollaboratorInput,
} from "./types/types";

//* Import types from CSVCollaborator-validator
import {
  type CSVCollaborator,
  type CSVCollaboratorProcessingError,
  type CSVCollaboratorProcessingResult,
} from "../../utils/csv/csv-validatorCollaborator";

export const useCollaborator = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });

  const queryKey = [
    "collaborator",
    {
      search,
      currentPage: pagination.currentPage,
      itemsPerPage: pagination.itemsPerPage,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      collaboratorAPI.getAll(
        search,
        pagination.currentPage,
        token,
        pagination.itemsPerPage
      ),
    staleTime: 5 * 60 * 1000,
  });

  const collaborator = data?.collaborators || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev) => ({ ...prev, currentPage: newPage }));
  }, []);

  const createCollaboratorMutation = useMutation({
    mutationFn: (collaboratorData: CreateCollaboratorInput) =>
      collaboratorAPI.create(collaboratorData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborator"] });
    },
  });

  const updateCollaboratorMutation = useMutation({
    mutationFn: ({ id, collaboratorData }: UpdateCollaboratorInput) =>
      collaboratorAPI.update(id, collaboratorData, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborator"] });
    },
  });

  const deleteCollaboratorMutation = useMutation({
    mutationFn: (id: string) => collaboratorAPI.delete(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborator"] });
    },
  });

  const getCollaboratorById = useCallback(
    async (id: string): Promise<CollaboratorsDetails | null> => {
      try {
        const collaboratorDetails = await collaboratorAPI.getById(id, token);
        return collaboratorDetails;
      } catch (err) {
        console.error("Error fetching collaborator by ID:", err);
        return null;
      }
    },
    [token]
  );

  const processCSVCollaboratorMutation = useMutation<
    CSVCollaboratorProcessingResult,
    Error,
    CSVCollaborator[]
  >({
    mutationFn: async (
      collaborator: CSVCollaborator[]
    ): Promise<CSVCollaboratorProcessingResult> => {
      try {
        const result = await collaboratorAPI.createBatch(collaborator, token);

        return {
          success: result.success,
          errors: result.errors.map((err: any) => ({
            id: err.id,
            collaborator: {
              name: err.collaborator.name,
              email: err.collaborator.email,
              isValid: false,
              is_restore: err.is_restore,
            },
            error: err.error || "Unknown error",
          })),
          total: result.total,
        };
      } catch (error: any) {
        // Captura os erros retornados pelo backend
        let errorMessage = "Erro desconhecido";
        let errors: CSVCollaboratorProcessingError[] = [];

        if (
          error.response &&
          error.response.data &&
          error.response.data.error
        ) {
          const backendError = error.response.data.error;
          errorMessage = backendError.message || errorMessage;

          // Se o backend retornar erros detalhados
          if (backendError.errors && Array.isArray(backendError.errors)) {
            errors = backendError.errors;
          }
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        return {
          success: 0,
          errors:
            errors.length > 0
              ? errors.map((err) => ({
                  id: err.id,
                  collaborator: {
                    name: err.collaborator?.name || "Nome não disponível",
                    email: err.collaborator?.email || "Email não disponível",
                    isValid: false,
                  },
                  error: err.error || errorMessage,
                  is_restore: err.collaborator.is_restore,
                }))
              : collaborator.map((collaborator, index) => ({
                  id: `error-${index}`,
                  collaborator: { ...collaborator, isValid: false },
                  error: errorMessage,
                  is_restore: false,
                })),
          total: collaborator.length,
        };
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborator"] });
    },
  });

  const restoreByBatchCollaboratorMutation = useMutation({
    mutationFn: (id: string) => collaboratorAPI.restoreByBatch(id, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborator"] });
    },
  });

  const restireByEmailCollaboratorMutation = useMutation({
    mutationFn: (email: string) => collaboratorAPI.restoreByEmail(email, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collaborator"] });
    },
  });

  return {
    collaborator,
    loading: isLoading || isFetching,
    error: isError ? error?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createCollaboratorAsync: createCollaboratorMutation.mutateAsync,
    updateCollaborator: updateCollaboratorMutation.mutate,
    deleteCollaborator: deleteCollaboratorMutation.mutate,
    getCollaboratorById,
    changePage,
    isCreating: createCollaboratorMutation.isPending,
    isUpdating: updateCollaboratorMutation.isPending,
    isDeleting: deleteCollaboratorMutation.isPending,
    processCSVCollaborator: processCSVCollaboratorMutation.mutateAsync,
    isProcessingCSVCollaborator: processCSVCollaboratorMutation.isPending,
    CSVCollaboratorError: processCSVCollaboratorMutation.error,
    restoreByBatchCollaborator: restoreByBatchCollaboratorMutation.mutate,
    restoreByEmailCollaborator: restireByEmailCollaboratorMutation.mutate,
  };
};
